// The focused-flow shell for onboarding screens 1-6 (slice-2-onboarding.md §2.2): a
// minimal top bar (back caret, title, step indicator) + one sticky primary action, not
// the full sidebar shell. Below `xl` no sidebar renders at all; the back caret is the
// only way out and it always works (no dead end mid-flow, §5 accessibility).
import type { ReactNode } from "react";

const BackIcon = (): React.JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

/** `onBack` is the single source of "where the caret goes" - each screen supplies its
 * own specific prior route (e.g. `/apps/new/intent`), so this never double-navigates
 * with a separate history.back(). Optional: a denied/gated state may render the shell
 * with no back destination of its own. */
export const FocusedFlowShell = ({
  title,
  onBack,
  children,
  cta,
}: {
  readonly title: string;
  readonly onBack?: () => void;
  readonly children: ReactNode;
  readonly cta?: ReactNode;
}): React.JSX.Element => {
  return (
    <div className="flow-shell">
      <a href="#flow-main" className="skip-link">
        Skip to main content
      </a>
      <div className="flow-topbar">
        <button type="button" className="flow-back" aria-label="Back" onClick={onBack}>
          <BackIcon />
        </button>
        <div className="flow-title">{title}</div>
      </div>
      <div className="flow-body" id="flow-main">
        {children}
      </div>
      {cta != null ? <div className="flow-cta-sticky">{cta}</div> : null}
    </div>
  );
};
